// frontend/src/pages/HomePageNew.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/SEO/PageSEO';
import { jsonLd } from '../components/UI/seo';
import { useLanguage } from '../hooks/useLanguage';
import { useDepartments, useDoctors, useFeaturedServices, formatPrice, useClinicInfo } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import SearchBar from '../components/Search/SearchBar';
import ClinicMapWidget from '../components/ClinicMap/ClinicMapWidget';
import DoctorCallButton from '../components/UI/DoctorCallButton';

function useCountUp(target: number, duration = 1400) {
  const [value, setValue] = React.useState(0);
  const rafRef = React.useRef<number>(0);
  const ref = React.useRef<HTMLElement | null>(null);

  const start = React.useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setValue(0);
    const startTime = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(eased * target));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [target, duration]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) start(); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); cancelAnimationFrame(rafRef.current); };
  }, [start]);

  return { value, ref };
}

function ClinicStatsCard({ theme, t }: { theme: string; t: (key: string) => string }) {
  const { value: years, ref: yearsRef } = useCountUp(15);

  const pills = [
    t('home.stats.features.hospital'),
    t('home.stats.features.outpatient'),
    t('home.stats.features.lab'),
    t('home.stats.features.surgery'),
    t('home.stats.features.location'),
    t('home.stats.features.insurance'),
    t('home.stats.features.gov'),
    t('home.stats.features.ct'),
  ];

  return (
    <div
      className="stats-card relative rounded-3xl overflow-hidden p-9 w-full lg:w-[416px] shrink-0"
      style={{
        background: theme === 'dark'
          ? 'radial-gradient(120% 120% at 0% 0%, #1e3a5f 0%, #0c2747 45%, #06152e 100%)'
          : 'radial-gradient(120% 120% at 0% 0%, #5aa0ff 0%, #0c63e9 45%, #072e6e 100%)',
        boxShadow: '0 20px 50px -18px rgba(7,46,110,0.6)',
      }}
    >
      {/* Декоративный блоб */}
      <div
        className="absolute -bottom-[100px] -right-[80px] size-[320px] pointer-events-none"
        style={{
          borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 30%, rgba(120,180,255,0.55), rgba(120,180,255,0) 70%)',
          animation: 'statsBlob 13s ease-in-out infinite',
        }}
      />

      <div className="relative">
        {/* Герой-цифра */}
        <div className="flex items-baseline gap-2.5 mb-5">
          <span
            ref={yearsRef as React.RefObject<HTMLSpanElement>}
            className="font-extrabold text-white leading-none"
            style={{ fontSize: '64px', textShadow: '0 6px 24px rgba(0,0,0,0.25)' }}
          >
            {years}+
          </span>
          <span className="font-semibold text-base text-white/85 max-w-[120px] leading-tight">
            {t('home.stats.years')}
          </span>
        </div>

        {/* Стеклянные таблетки */}
        <div className="flex flex-wrap gap-[11px] items-center">
          {pills.map((item) => (
            <div
              key={item}
              className="stats-pill px-[15px] py-2 rounded-full text-white text-base cursor-default"
              style={{
                background: 'rgba(255,255,255,0.16)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.35)',
              }}
            >
              {item}
            </div>
          ))}
        </div>

        <Link
          to="/about"
          className="flex items-center gap-1 text-white font-medium text-xl hover:underline w-full mt-[22px]"
        >
          {t('home.stats.aboutClinic')}
          <svg className="stats-arrow w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

const HomePageNew: React.FC = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { data: clinicInfo } = useClinicInfo();
  const clinic = clinicInfo?.[0];
  const { data: departments } = useDepartments();
  const { data: doctors } = useDoctors();
  const { data: featuredServices, isLoading: isLoadingFeatured } = useFeaturedServices();

  return (
    <>
      <PageSEO
        title={t('clinic.welcome')}
        description={clinic?.description || t('clinic.description')}
        path="/"
      >
        {clinic && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd.organization(clinic))}
          </script>
        )}
      </PageSEO>

      <div className="relative overflow-x-hidden">

        {/* Hero Section */}
        <section className="relative z-10 px-4 sm:px-8 py-8">
          <div className="max-w-[1312px] mx-auto flex flex-col gap-8">

            {/* Hero Banner */}
            <div
              className="relative rounded-3xl overflow-hidden px-6 py-[88px] flex flex-col items-center gap-14"
              style={{
                background: 'radial-gradient(ellipse at 50% -5%, #5b9bff 0%, #448af3 25%, #2d6be7 50%, #1753db 75%, #0b47d5 87.5%, #003bcf 100%)',
              }}
            >
              {/* Декоративные элементы из Figma */}
              {/* image 15 — синяя волна сверху-слева */}
              <div className="absolute flex h-[754px] items-center justify-center left-[-531px] top-[-263px] w-[1159px] pointer-events-none">
                <div className="-scale-y-100 flex-none rotate-180">
                  <img alt="" className="h-[754px] w-[1159px] object-cover" src="/images/decorative/hero-wave.webp" />
                </div>
              </div>
              {/* image 16 — синяя волна снизу-справа */}
              <div className="absolute bottom-0 flex h-[754px] items-center justify-center right-[-842px] w-[1159px] pointer-events-none">
                <div className="-scale-y-100 flex-none rotate-180">
                  <img alt="" className="h-[754px] w-[1159px] object-cover" src="/images/decorative/hero-wave.webp" />
                </div>
              </div>
              {/* Ellipse — синий размытый круг, soft-light */}
              <div className="absolute bottom-[-231px] mix-blend-soft-light right-[-26px] size-[656px] pointer-events-none">
                <div className="absolute inset-[-24.62%]">
                  <img alt="" className="block max-w-none size-full" src="/images/decorative/hero-ellipse.svg" />
                </div>
              </div>

              {/* Heading */}
              <div className="relative flex flex-col items-center gap-3 text-center">
                <h1 className="font-bold text-[#eeeef0] leading-none" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
                  {clinic?.name || t('home.hero.titleAccent')}
                </h1>
                <p className="text-xl text-[#eeeef0] leading-6 text-center">
                  {clinic?.description ? (
                    <span dangerouslySetInnerHTML={{ __html: clinic.description }} />
                  ) : (
                    t('home.hero.subtitle')
                  )}
                </p>
              </div>

              {/* Search + CTA */}
              <div className="relative flex flex-col items-center gap-12 w-full">
                <div className="w-full max-w-[800px]">
                  <SearchBar
                    placeholder={t('home.hero.searchPlaceholder')}
                    size="large"
                    variant="hero"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/services"
                    className="bg-[#fdfdfd] text-[#0c63e9] font-medium text-base px-4 py-4 rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    {t('home.hero.ctaSecondary')}
                  </Link>
                  <Link
                    to="/doctors"
                    className="border border-[#fdfdfd] text-[#eeeef0] font-medium text-base px-4 py-4 rounded-xl hover:bg-white/10 transition-colors whitespace-nowrap self-stretch flex items-center justify-center"
                  >
                    {t('home.hero.ctaDoctors')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Clinic Map Widget */}
            <ClinicMapWidget />

            {/* Below hero: Services + Stats */}
            <div className="flex flex-col lg:flex-row gap-8 items-stretch">

              {/* Popular Services */}
              <div className="bg-[#edeef2] dark:bg-[#192e4d] rounded-3xl p-5 sm:p-9 flex flex-col gap-4 flex-1">
                <h2 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7">
                  {t('home.sections.popularServices.title')}
                </h2>

                <div className="flex flex-col gap-2 flex-1 min-h-[224px]">
                  {isLoadingFeatured ? (
                    [1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="flex items-center justify-between px-3 sm:px-5 py-2 rounded-xl animate-pulse">
                        <div className="h-5 bg-gray-300 dark:bg-[#2d3741] rounded w-2/3" />
                        <div className="h-5 bg-gray-300 dark:bg-[#2d3741] rounded w-16" />
                      </div>
                    ))
                  ) : featuredServices?.slice(0, 6).map((service, index) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.slug}`}
                      className={`flex flex-col sm:flex-row sm:items-start sm:justify-between px-3 sm:px-5 py-2 rounded-xl hover:bg-[#c2dbff] dark:hover:bg-[#192e4d] transition-colors group ${index === 0 ? 'bg-[#c2dbff] dark:bg-[#192e4d]' : ''}`}
                    >
                      <span className={`flex-1 min-w-0 text-base font-medium text-[#0c63e9] dark:text-[#599bff] break-words sm:mr-2 ${index === 0 ? 'font-bold' : ''}`}>
                        {service.name}
                      </span>
                      <div className="flex items-center gap-1 shrink-0 mt-0.5 sm:mt-0">
                        <span className="text-[#0c63e9] dark:text-[#599bff] font-bold text-base whitespace-nowrap">
                          {formatPrice(service.price)}
                        </span>
                        <svg className="w-6 h-6 text-[#0c63e9] dark:text-[#599bff] shrink-0 -rotate-90 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>

                <Link to="/services" className="flex items-center gap-1 text-[#0c63e9] dark:text-[#599bff] font-medium text-xl hover:underline">
                  {t('home.sections.popularServices.viewAll')}
                  <svg className="w-6 h-6 -rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Link>
              </div>

              {/* Clinic Stats — вариант C: герой-цифра + стеклянные таблетки */}
              <ClinicStatsCard theme={theme} t={t} />
            </div>
          </div>
        </section>

        {/* Departments Section */}
        <section className="relative z-10 px-4 sm:px-8 pt-8 pb-4">
          <div className="max-w-[1312px] mx-auto flex flex-col gap-8">

            <div className="flex flex-col gap-6">
              <div>
                <h2 className="text-[#0c63e9] dark:text-[#599bff] text-5xl font-bold leading-[56px]">
                  {t('home.sections.departments.title')}
                </h2>
                <p className="text-[#454545] dark:text-[#e4e4e4] text-xl mt-4 leading-6">
                  {t('home.sections.departments.subtitle')}
                </p>
              </div>

              <div className="flex flex-wrap gap-8 justify-center">
                {departments?.slice(0, 6).map((department) => (
                  <Link
                    key={department.id}
                    to={`/departments/${department.slug}`}
                    className="group bg-[#edeef2] dark:bg-[#1f1f1f] rounded-3xl p-9 flex flex-col justify-between overflow-hidden w-full sm:w-[calc(50%-16px)] lg:w-[416px] h-[280px] hover:shadow-lg transition-shadow"
                  >
                    {department.icon ? (
                      <img src={department.icon} alt="" className="w-[64px] h-[64px]" />
                    ) : (
                      <div className="bg-[#0c63e9] dark:bg-[#004dc3] rounded-full p-3 w-fit">
                        <svg className="w-[38px] h-[38px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex flex-col gap-3">
                      <h3 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7 group-hover:underline">
                        {department.name}
                      </h3>
                      <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6 line-clamp-2">
                        {department.short_description || department.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/departments" className="flex items-center gap-1 text-[#0c63e9] dark:text-[#599bff] font-medium text-xl hover:underline self-start">
              {t('home.sections.departments.viewAll')}
              <svg className="w-6 h-6 -rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
          </div>
        </section>

        {/* Doctors Section */}
        <section className="relative z-10 px-4 sm:px-8 pt-8 pb-4">
          <div className="max-w-[1312px] mx-auto flex flex-col gap-8">

            <div className="text-center flex flex-col gap-6">
              <div>
                <h2 className="text-[#0c63e9] dark:text-[#599bff] text-5xl font-bold leading-9">
                  {t('home.sections.doctors.title')}
                </h2>
                <p className="text-[#454545] dark:text-[#e4e4e4] text-xl mt-4 leading-6">
                  {t('home.sections.doctors.subtitle')}
                </p>
              </div>

              <div className="flex flex-wrap gap-8 justify-center">
                {doctors?.slice(0, 3).map((doctor) => {
                  const photoEl = doctor.photo ? (
                    <img src={doctor.photo} alt={doctor.full_name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-24 h-24 text-[#0c63e9]/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  );
                  const actionsEl = (
                    <div className="flex flex-wrap gap-[10px] items-center">
                      <Link to={`/doctors/${doctor.slug}`}
                        className="bg-[#0c63e9] dark:bg-[#004dc3] text-[#eeeef0] font-medium text-base px-4 py-4 rounded-xl flex items-center gap-2 hover:bg-[#0b47d5] transition-colors shrink-0">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {t('home.sections.doctors.book')}
                      </Link>
                      <DoctorCallButton
                        doctorPhone={doctor.phone}
                        clinicPhone={clinic?.phones?.[0]}
                        hidden={doctor.departments?.some(d => d.slug === 'partners')}
                        variant="icon"
                      />
                    </div>
                  );

                  return (
                    <div key={doctor.id} className="group bg-[#edeef2] dark:bg-[#1f1f1f] rounded-3xl w-full sm:w-[calc(50%-16px)] lg:w-[416px] flex flex-col p-[36px] gap-[16px] relative overflow-hidden">

                      {/* Обычное состояние: фото в рамке + текст под ним */}
                      <div className="rounded-[22px] overflow-hidden shrink-0 transition-opacity duration-900 group-hover:opacity-0"
                        style={{ height: '280px', background: theme === 'dark' ? 'linear-gradient(160deg, #192e4d 0%, #1a3560 50%, #1b3c73 100%)' : 'linear-gradient(160deg, #cce0f5 0%, #b8d4ee 50%, #9fc4e6 100%)' }}>
                        {photoEl}
                      </div>
                      <div className="flex flex-col gap-[16px] transition-opacity duration-900 group-hover:opacity-0">
                        <div className="flex flex-col gap-1">
                          <h3 className="text-[#454545] dark:text-[#e4e4e4] text-2xl font-bold leading-7">{doctor.full_name}</h3>
                          <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">{doctor.specialization}</p>
                        </div>
                        {actionsEl}
                      </div>

                      {/* Ховер: фото на всю карточку + текст overlay */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-900 pointer-events-none group-hover:pointer-events-auto">
                        <div className="absolute inset-0" style={{ background: theme === 'dark' ? 'linear-gradient(160deg, #192e4d 0%, #1a3560 50%, #1b3c73 100%)' : 'linear-gradient(160deg, #cce0f5 0%, #b8d4ee 50%, #9fc4e6 100%)' }}>
                          {photoEl}
                        </div>
                        <div className="absolute bottom-[24px] left-[24px] right-[24px] p-[24px] rounded-[22px] flex flex-col gap-[16px]" style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.80)' : 'rgba(255,255,255,0.60)', backdropFilter: 'blur(8px)' }}>
                          <div className="flex flex-col gap-1">
                            <h3 className="text-[#454545] dark:text-[#e4e4e4] text-2xl font-bold leading-7">{doctor.full_name}</h3>
                            <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">{doctor.specialization}</p>
                          </div>
                          {actionsEl}
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>

            <Link to="/doctors" className="flex items-center gap-1 text-[#0c63e9] dark:text-[#599bff] font-medium text-xl hover:underline self-center">
              {t('home.sections.doctors.viewAll')}
              <svg className="w-6 h-6 -rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
          </div>
        </section>

        {/* How to Book Section */}
        <section className="relative z-10 px-4 sm:px-8 pt-8 pb-16">
          <div className="max-w-[1312px] mx-auto flex flex-col gap-8">

            <div>
              <h2 className="text-[#0c63e9] dark:text-[#599bff] text-5xl font-bold leading-9">
                {t('home.sections.howToBook.title')}
              </h2>
              <p className="text-[#454545] dark:text-[#e4e4e4] text-xl mt-6 leading-6">
                {t('home.sections.howToBook.subtitle')}
              </p>
            </div>

            {/* Steps grid */}
            <div className="flex flex-col lg:flex-row gap-8">

              {/* Step 1 - Large */}
              <div className="relative rounded-3xl overflow-hidden flex items-end p-[18px] h-[680px] flex-[2]">
                {/* image 23 — женщина за ноутбуком */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <img alt="" className="absolute inset-0 w-full h-full object-cover" src="/images/decorative/booking-step1.jpg" />
                </div>
                <div
                  className="relative rounded-[22px] p-[18px] flex flex-col gap-4 min-w-[280px]"
                  style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.85)' : 'rgba(237,238,242,0.75)', backdropFilter: 'blur(10px)', boxShadow: '4px 4px 19px 0px rgba(0,60,156,0.15)' }}
                >
                  <div className="bg-[#0c63e9] dark:bg-[#004dc3] rounded-full w-[60px] h-[60px] flex items-center justify-center shrink-0">
                    <span className="text-[#fdfdfd] text-2xl font-bold">1</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    <h3 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7">{t('home.sections.howToBook.step1Title')}</h3>
                    <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">{t('home.sections.howToBook.step1Text')}</p>
                  </div>
                </div>
              </div>

              {/* Steps 2 & 3 */}
              <div className="flex flex-col gap-8 flex-1">

                {/* Step 2 */}
                <div className="relative rounded-3xl overflow-hidden flex items-end p-[18px] flex-1 min-h-[280px]" style={{ background: '#0067fc' }}>
                  {/* image 25 — часы */}
                  <div className="absolute -translate-x-1/2 left-1/2 top-[-132px] w-[450px] h-[324px] pointer-events-none">
                    <img alt="" className="absolute inset-0 w-full h-full object-cover" src="/images/decorative/booking-step2.jpg" />
                  </div>
                  <div
                    className="relative rounded-[22px] p-[18px] flex flex-col gap-4 min-w-[280px]"
                    style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.85)' : 'rgba(237,238,242,0.75)', backdropFilter: 'blur(10px)', boxShadow: '4px 4px 19px 0px rgba(0,60,156,0.15)' }}
                  >
                    <div className="bg-[#0c63e9] dark:bg-[#004dc3] rounded-full w-[60px] h-[60px] flex items-center justify-center shrink-0">
                      <span className="text-[#fdfdfd] text-2xl font-bold">2</span>
                    </div>
                    <div className="flex flex-col gap-3">
                      <h3 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7">{t('home.sections.howToBook.step2Title')}</h3>
                      <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">{t('home.sections.howToBook.step2Text')}</p>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="relative rounded-3xl overflow-hidden flex items-end p-[18px] flex-1 min-h-[280px] bg-[#edeef2] dark:bg-[#192e4d]">
                  {/* image 24 — женщина по телефону */}
                  <div className="absolute -translate-y-1/2 left-[-28.61%] right-[-12.98%] top-[calc(50%+30px)] pointer-events-none" style={{ aspectRatio: '589/616' }}>
                    <img alt="" className="absolute inset-0 w-full h-full object-cover" src="/images/decorative/booking-step3.jpg" />
                  </div>
                  <div
                    className="relative rounded-[22px] p-[18px] flex flex-col gap-4"
                    style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.85)' : 'rgba(237,238,242,0.75)', backdropFilter: 'blur(10px)', boxShadow: '4px 4px 19px 0px rgba(0,60,156,0.15)' }}
                  >
                    <div className="bg-[#0c63e9] dark:bg-[#004dc3] rounded-full w-[60px] h-[60px] flex items-center justify-center shrink-0">
                      <span className="text-[#fdfdfd] text-2xl font-bold">3</span>
                    </div>
                    <div className="flex flex-col gap-3">
                      <h3 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7">{t('home.sections.howToBook.step3Title')}</h3>
                      <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">{t('home.sections.howToBook.step3Text')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone booking banner */}
            <div
              className="relative rounded-3xl overflow-hidden p-[18px] flex flex-col sm:flex-row items-stretch gap-3"
              style={{ background: theme === 'dark' ? 'rgba(30,50,64,0.92)' : 'rgba(160,195,219,0.75)', backdropFilter: 'blur(6px)' }}
            >
              {/* Ellipse декоративные */}
              <div className="absolute bottom-[-178px] right-[1054px] size-[392px] pointer-events-none">
                <div className="absolute inset-[-41.2%]">
                  <img alt="" className="block max-w-none size-full" src="/images/decorative/booking-ellipse.svg" />
                </div>
              </div>
              <div className="absolute bottom-[-21px] right-[-255px] size-[392px] pointer-events-none">
                <div className="absolute inset-[-41.2%]">
                  <img alt="" className="block max-w-none size-full" src="/images/decorative/booking-ellipse.svg" />
                </div>
              </div>
              {/* Левая карточка — текст */}
              <div
                className="relative rounded-[22px] p-[18px] flex flex-col gap-3 flex-1"
                style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.85)' : 'rgba(237,238,242,0.75)', backdropFilter: 'blur(10px)', boxShadow: '4px 4px 19px 0px rgba(0,60,156,0.15)' }}
              >
                <h3 className="text-[#0c63e9] dark:text-[#599bff] text-2xl font-bold leading-7">
                  {t('home.sections.howToBook.phoneTitle')}
                </h3>
                <p className="text-[#454545] dark:text-[#e4e4e4] text-base leading-6">
                  {t('home.sections.howToBook.phoneSubtitle')}
                </p>
              </div>
              {/* Правая карточка — номера телефонов */}
              {(clinic?.phones?.length ?? 0) > 0 && (
                <div
                  className="relative rounded-[22px] p-[18px] flex flex-wrap content-center gap-x-6 gap-y-2 flex-1"
                  style={{ background: theme === 'dark' ? 'rgba(25,46,77,0.85)' : 'rgba(237,238,242,0.75)', backdropFilter: 'blur(10px)', boxShadow: '4px 4px 19px 0px rgba(0,60,156,0.15)' }}
                >
                  {clinic?.phones?.map((phone) => (
                    <a
                      key={phone}
                      href={`tel:${phone}`}
                      className="flex items-center gap-3 text-[#0c63e9] dark:text-[#599bff] font-bold text-xl leading-7 hover:opacity-80 transition-opacity"
                    >
                      <svg className="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V21a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
                      </svg>
                      {phone}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-3 items-center justify-center">
              <Link
                to="/services"
                className="bg-[#0c63e9] dark:bg-[#004dc3] text-[#eeeef0] font-medium text-base px-4 py-4 rounded-xl flex items-center gap-2 hover:bg-[#0b47d5] transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('home.sections.howToBook.ctaService')}
              </Link>
              <Link
                to="/doctors"
                className="border border-[rgba(160,195,219,0.75)] dark:border-[rgba(30,50,64,0.75)] text-[#0c63e9] dark:text-[#599bff] font-medium text-base px-4 py-4 rounded-xl hover:bg-[#edeef2] dark:hover:bg-[#1f1f1f] transition-colors"
              >
                {t('home.sections.howToBook.ctaDoctor')}
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default HomePageNew;
